import { useMemo, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  AppView,
  UserLevel,
  TeamCapability,
  TeamRole,
  UserRole,
  ViewEligibility,
} from '@aegislab/shared';
import { getAppNavSections, isTeaserView } from '@/shared/config';
import { useGlitchLabel } from '@/shared/lib/useGlitchLabel';
// 배럴(`@/shared/ui`) 경유 금지 — 같은 세그먼트라 mega-barrel 자기참조 순환이 된다
import { getAppViewIcon } from '../../icons';
import * as s from './QuickNav.css';

// --- Props ---

/** 기본값을 렌더마다 새 배열로 만들면 목록 계산이 매번 다시 돈다. */
const NO_ELIGIBILITIES: ViewEligibility[] = [];

interface QuickNavProps {
  activeView?: AppView;
  onViewChange?: (view: AppView) => void;
  userLevel?: UserLevel;
  /** 역할 요건이 걸린 뷰(대표 콘솔)를 목록에서 가르는 축 — 미확정이면 PLAYER 로 본다 */
  roles?: UserRole[];
  /** 권한 요건이 걸린 뷰(근태 운영)는 팀 직책이 권한을 주므로 목록도 그 축을 본다 */
  teamRole?: TeamRole;
  teamCapabilities?: TeamCapability[];
  /** 서버가 판정한 창 자격(명단 내려받기). 확인 전·실패는 [] — 그 항목은 숨긴 채로 둔다 */
  viewEligibilities?: ViewEligibility[];
  /** 어느 팀(TF 포함)에서든 팀장인가 — 팀 운영 창 노출에 쓴다. */
  leadsTeam?: boolean;
  /** 캐퍼빌리티 조회가 아직 확정되지 않음 — 항목 부재를 "권한 없음"으로 단정하지 않는다 */
  capabilitiesPending?: boolean;
  /** 캐퍼빌리티 조회 실패 — caps 가 [] 로 붕괴한 상태. 숨김은 유지하되 이유를 말한다 */
  capabilitiesError?: boolean;
  onRetryCapabilities?: () => void;
}

function QuickNavItem({
  view,
  label,
  isActive,
  teaser,
  onSelect,
}: {
  view: AppView;
  label: string;
  isActive: boolean;
  teaser: boolean;
  onSelect: () => void;
}) {
  const displayLabel = useGlitchLabel(label, teaser, 0.5);
  return (
    <button
      type="button"
      className={`${s.menuItem}${isActive ? ` ${s.menuItemActive}` : ''}`}
      onClick={onSelect}
      aria-current={isActive ? 'page' : undefined}
      aria-label={teaser ? `${label} (출시 예정)` : label}
    >
      <span className={s.menuItemIcon} aria-hidden="true">
        {getAppViewIcon(view, 22)}
      </span>
      <span
        className={s.menuItemLabel}
        aria-hidden={teaser || undefined}
        style={teaser ? { fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' } : undefined}
      >
        {displayLabel}
      </span>
    </button>
  );
}

export function QuickNav({
  activeView = 'quests',
  onViewChange,
  userLevel = 'INTERN',
  roles = ['PLAYER'],
  teamRole,
  teamCapabilities = [],
  viewEligibilities = NO_ELIGIBILITIES,
  leadsTeam = false,
  capabilitiesPending = false,
  capabilitiesError = false,
  onRetryCapabilities,
}: QuickNavProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLElement | null>(null);
  const fabRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();

  // 데스크톱 dock 과 **같은 소스**. 여기에 하드코딩 목록을 되살리지 말 것 —
  // 이원화가 곧 "모바일에서만 못 여는 화면" 을 만든다.
  const sections = useMemo(
    () =>
      getAppNavSections({
        userLevel,
        teamCapabilities,
        roles,
        teamRole,
        viewEligibilities,
        leadsTeam,
      }),
    [userLevel, teamCapabilities, roles, teamRole, viewEligibilities, leadsTeam]
  );

  // 확정 전에는 [] 를 근거로 "미출시"라고 단정하지 않는다 — 배지만 보류하고 항목은 그대로 둔다.
  const capabilitiesSettled = !capabilitiesPending && !capabilitiesError;

  const toggleMenu = useCallback(() => setMenuOpen((prev) => !prev), []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    requestAnimationFrame(() => {
      fabRef.current?.focus();
    });
  }, []);

  const handleSelect = useCallback(
    (view: AppView) => {
      if (activeView === view) {
        onViewChange?.('world-quests');
      } else {
        onViewChange?.(view);
      }
      setMenuOpen(false);
    },
    [activeView, onViewChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleMenu();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
      }
    },
    [closeMenu, toggleMenu]
  );

  useEffect(() => {
    if (!menuOpen) return;

    const firstButton = menuRef.current?.querySelector<HTMLButtonElement>('button');
    firstButton?.focus();

    const handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [closeMenu, menuOpen]);

  return createPortal(
    <>
      {/* Backdrop when open */}
      {menuOpen && <div className={s.backdrop} aria-hidden="true" onClick={closeMenu} />}

      {/* FAB button */}
      <button
        ref={fabRef}
        type="button"
        className={`${s.fabButton}${menuOpen ? ` ${s.fabButtonOpen}` : ''}`}
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        aria-expanded={menuOpen}
        aria-controls={menuOpen ? menuId : undefined}
        aria-label={menuOpen ? '빠른 탐색 닫기' : '빠른 탐색 열기'}
      >
        {getAppViewIcon(activeView, 24)}
      </button>

      {/* Quick navigation list */}
      {menuOpen && (
        <nav ref={menuRef} id={menuId} className={s.menu} aria-label="빠른 탐색">
          {capabilitiesError ? (
            <div role="alert" className={s.capabilityNotice}>
              <p className={s.capabilityNoticeText}>권한 정보를 불러오지 못했습니다</p>
              <button
                type="button"
                className={s.capabilityRetryButton}
                onClick={onRetryCapabilities}
              >
                다시 시도
              </button>
            </div>
          ) : capabilitiesPending ? (
            <p role="status" className={s.capabilityNotice}>
              권한 확인 중
            </p>
          ) : null}
          {sections.map((section) => (
            <section key={section.id} className={s.menuSection}>
              <h2 className={s.sectionHeading}>{section.label}</h2>
              <ul className={s.sectionList}>
                {section.items.map((item) => (
                  <li key={item.id}>
                    <QuickNavItem
                      view={item.id}
                      label={item.label}
                      isActive={activeView === item.id}
                      teaser={capabilitiesSettled && isTeaserView(item.id, teamCapabilities)}
                      onSelect={() => handleSelect(item.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
      )}
    </>,
    document.body
  );
}
